import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { Button, InputField } from 'src/components';
import { generateUniqueDeviceId } from 'src/components/Common/UniqueUserIdGenerator';
import { useCreateLogbookEntryMutation } from 'src/rtk/features/e-log-book/traineeAcademicActivitiesApi';
import * as Yup from 'yup';
import loader from '../../../../assets/images/loader.gif';

interface Field {
  id: string;
  name: string;
  placeholder?: string;
  type: string;
  options?: LogbookEntryDetails[];
  required?: number;
}

const DynamicForm = (props: any) => {
  const {
    dynamicFormProps,
    formName,
    systemTypeName,
    caseTypeName,
    skillTypeName,
    competencyGroup,
    entrySetupMasterId,
    setForm,
  } = props;

  const history = useHistory();

  const [submitIndex, setSubmitIndex] = useState<number>(1);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [uniqueDeviceId, setUniqueDeviceId] = useState<string>('');

  const InputFields: Field[] = dynamicFormProps.map(
    (field: any, index: number) => {
      return {
        name: `${field['column_name']?.replace('.', '')}`,
        placeholder: field.details_text,
        type: field.column_value_type,
        options: field.options,
        required: field.required_ind,
      };
    },
  );

  //   const { data: form, isError: isFormError } = useGetLogBookMasterSetupQuery(formId);
  //   const { data: caseType, isError: isCaseTypeError } = useGetLogbookEntrySetupMasterQuery(caseTypeId);
  //   const { data: skillType, isError: isSkillTypeError } = useGetLogbookEntrySetupMasterQuery(skillTypeId);

  const [
    createLogbookEntry,
    {
      data: logbookEntry,
      error: logbookEntryError,
      isSuccess: isLogbookEntrySuccess,
      isError: isLogbookEntryError,
      isLoading: isLogbookEntryLoading,
    },
  ] = useCreateLogbookEntryMutation();

  type FormValues = {
    [key: string]: string | number | boolean | undefined;
  };

  const entryError: CustomError = logbookEntryError as CustomError;

  const initialValues = React.useMemo(() => {
    const fieldsObj = InputFields.reduce((acc: FormValues, field: Field) => {
      switch (field.type) {
        case 'text':
        case 'textarea':
        case 'email':
        case 'date':
        case 'file':
          acc[`${field['name']}`] = '';
          break;
        case 'number':
          acc[field['name']] = 0;
          break;
        case 'checkbox':
          acc[field['name']] = false;
          break;
        case 'select':
          if (field.options?.length! > 0) {
            acc[field.name] = '';
          }
          break;
        case 'radio':
          if (field.options?.length! > 0) {
            acc[field.name] = '';
          }
          break;
        default:
          acc[field['name']] = undefined;
          break;
      }

      return acc;
    }, {});

    return fieldsObj;
  }, [InputFields]);

  const generateValidationSchema = (inputFields: any) => {
    const validationSchema: Record<string, any> = {};

    inputFields.forEach((field: any) => {
      if (
        field.required &&
        (field.type === 'text' ||
          field.type === 'textarea' ||
          field.type === 'date' ||
          field.type === 'email' ||
          field.type === 'file')
      ) {
        validationSchema[field['name']] = Yup.string().required(
          `${field['name']} is required`,
        );
      } else if (field.required && field.type === 'select') {
        if (field.options?.length! > 0) {
          validationSchema[field['name']] = Yup.string().required(
            `${field['name']} is required`,
          );
        }
      } else if (field.required && field.type === 'radio') {
        if (field.options?.length! > 0) {
          validationSchema[field['name']] = Yup.string().required(
            `${field['name']} is required`,
          );
        }
      }
    });

    return Yup.object().shape(validationSchema);
  };

  const getBase64 = (file: any) => new Promise((resolve, reject) => {
    var reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

  const decodeBase64EncodeString = (base64String: string) => {
    let correctBase64EncodedString: string = base64String.split("base64,")?.[1];
    let tempString: string = base64String.split("base64,")?.[0];
    let typeOfDocument: string = tempString.split("data:")?.[1] + ';base64';
    let byteCharacters = atob(correctBase64EncodedString);
    let byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    let byteArray = new Uint8Array(byteNumbers);
    let file = new Blob([byteArray], { type: typeOfDocument });
    let fileURL = URL.createObjectURL(file);
    window.open(fileURL);
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema:
      InputFields.length > 0 ? generateValidationSchema(InputFields) : null,
    onSubmit: (values) => {
      setSubmitted(true);
      let detailsJson: any = {};

      if (formName) {
        detailsJson['form'] = formName;
      }

      if (systemTypeName) {
        detailsJson['systemType'] = systemTypeName;
      }

      if (caseTypeName) {
        detailsJson['topicType'] = caseTypeName;
      }
      if (skillTypeName) {
        detailsJson['skillType'] = skillTypeName;
      }
      if (systemTypeName) {
        detailsJson['systemType'] = systemTypeName;
      }

      const sortedValues: any = {};
      InputFields?.forEach((i: Field) => {
        sortedValues[i?.name] = values[i?.name];
      });

      const finalSubmittedValue = {
        entry_setup_master_id: entrySetupMasterId,
        heading: formName,
        details_json: JSON.stringify(
          {
            entry_setup_master_id: entrySetupMasterId,
            ...detailsJson,
            ...sortedValues,
          },
          null,
          2,
        ),
        submit_ind: submitIndex,
        device_info: uniqueDeviceId,
        log_type: 2, // 2 for Academic Activities
      };

      createLogbookEntry({
        ...finalSubmittedValue,
      });

      // alert(JSON.stringify(values, null, 2));
    },
  });

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    handleReset,
    setValues,
    setFieldValue,
    getFieldProps,
    values,
    touched,
    errors,
    resetForm,
    setErrors,
  } = formik;

  useEffect(() => {
    const generateDeviceId: Promise<string> = generateUniqueDeviceId();
    generateDeviceId.then((value: string) => {
      setUniqueDeviceId(value);
    });
  }, []);

  useEffect(() => {
    dynamicFormProps?.forEach((field: any) => {
      if (field.column_value_type === 'date') {
        setFieldValue(`${field['column_name']}`, new Date(Date.now()).toISOString().slice(0, 10));
      }
    });
  }, [dynamicFormProps]);

  useEffect(() => {
    if (isLogbookEntrySuccess) {
      setSubmitted(false);
      toast.success('Logbook entry successful');
      history.push('/trainee/logbook/academic-activities');
    }
    if (isLogbookEntryError) {
      setSubmitted(false);
      toast.error(entryError?.message || 'Something went wrong!');
    }
  }, [isLogbookEntrySuccess, isLogbookEntryError]);

  return (
    <>
      {isLogbookEntryLoading ? (
        <div className="overlay">
          <img src={loader} alt="Loading" height={100} width={300} />
        </div>
      ) : null}
      <Form
        className="custom-form"
        onSubmit={(e: any) => {
          e.preventDefault();
          handleSubmit();
        }}
        onReset={(e: any) => {
          e.preventDefault();
          setForm({ id: '' });
          resetForm();
        }}
      >
        <div className="row">
          {InputFields.map((field, index) => {
            switch (field.type) {
              case 'checkbox':
                return (
                  <div key={index} className="col-lg-4 col-md-6 col-sm-12">
                    <FormGroup check className="mb-3 mt-4">
                      <Label check>
                        <Input
                          type={field.type}
                          checked={!!values[field['name']]}
                          {...getFieldProps(field['name'])}
                        />{' '}
                        {field['name']}
                      </Label>
                    </FormGroup>
                  </div>
                );
              case 'radio':
                return field.options?.length! > 0 ? (
                  <div key={field.id} className="col-sm-12">
                    <Label className={field.required ? "required-field form-label" : "form-label"}>{field['name']}</Label>
                    <br />
                    {field.options?.map((option: any) => {
                      return (
                        <FormGroup check inline key={option.id}>
                          <Input
                            id={option.column_name}
                            name={field['name']}
                            type={field.type as InputType}
                            value={option.details_text}
                            onChange={(e) => {
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                          />

                          <Label check for={option.column_name}>
                            {option.column_name}
                          </Label>
                        </FormGroup>
                      );
                    })}
                  </div>
                ) : null;
              case 'file':
                return (
                  <div key={index} className="col-lg-4 col-md-6 col-sm-12">
                    <FormGroup className="mb-3">
                      <Label className={field.required ? "required-field form-label me-2" : "form-label me-2"}>{field['name']}</Label>
                      {typeof values?.[field['name']] === 'string' &&
                        values?.[field['name']] !== '' ? (
                        <a
                          className='btn btn-sm btn-outline-info my-2'
                          rel='noreferrer'
                          target='_blank'
                          onClick={(e) => {
                            decodeBase64EncodeString(values?.[field['name']] as string);
                          }}
                        >
                          View
                        </a>
                      ) : (
                        null
                      )}
                      <InputField
                        type={field.type as InputType}
                        name={field['name']}
                        placeholder={field.placeholder}
                        onChange={(e) => {
                          getBase64(e.target?.files[0]).then((value) => {
                            setFieldValue(field['name'], value);
                            console.log(value);
                          }).catch((err) => {
                            setFieldValue(field['name'], '');
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={{ errors, touched }}
                      />
                    </FormGroup>
                  </div>
                );
              case 'select':
                return field.options?.length! > 0 ? (
                  <div key={field.id} className="col-lg-4 col-md-6 col-sm-12">
                    <FormGroup className="mb-3">
                      <Label for={field['name']} className={field.required ? "required-field form-label" : "form-label"}>{field['name']}</Label>
                      <InputField
                        type={field.type as InputType}
                        placeholder={field.placeholder}
                        options={field.options?.map((option: any) => {
                          return {
                            label: option.column_name,
                            value: option.details_text,
                          };
                        })}
                        invalid={{ errors, touched }}
                        {...getFieldProps(field['name'])}
                      />
                    </FormGroup>
                  </div>
                ) : null;
              case 'date':
                return (
                  ['date of birth', 'dob'].includes(field['name'].toLowerCase()) ?
                    <div key={index} className="col-lg-4 col-md-6 col-sm-12">
                      <FormGroup className="mb-3">
                        <Label for={field['name']} className={field.required ? "required-field form-label" : "form-label"}>{field['name']}</Label>
                        <InputField
                          type={field.type as InputType}
                          placeholder={field.placeholder}                          
                          invalid={{ errors, touched }}
                          {...getFieldProps(`${field['name']}`)}
                        />
                      </FormGroup>
                    </div>
                    :
                    <div key={index} className="col-lg-4 col-md-6 col-sm-12">
                      <FormGroup className="mb-3">
                        <Label for={field['name']} className={field.required ? "required-field form-label" : "form-label"}>{field['name']}</Label>
                        <InputField
                          type={field.type as InputType}
                          placeholder={field.placeholder}
                          max={new Date(Date.now()).toISOString().slice(0, 10)}
                          invalid={{ errors, touched }}
                          {...getFieldProps(`${field['name']}`)}
                        />
                      </FormGroup>
                    </div>
                );
              default:
                return (
                  <div key={index} className="col-lg-4 col-md-6 col-sm-12">
                    <FormGroup className="mb-3">
                      <Label for={field['name']} className={field.required ? "required-field form-label" : "form-label"}>{field['name']}</Label>
                      <InputField
                        type={field.type as InputType}
                        placeholder={field.placeholder}
                        invalid={{ errors, touched }}
                        {...getFieldProps(`${field['name']}`)}
                      />
                    </FormGroup>
                  </div>
                );
            }
          })}

          {/* {competencyGroup ? (
          <CompetencyGroupAndDetails
            formik={formik}
            caseTypeName={caseTypeName}
            skillTypeName={skillTypeName}
            compentencyGroup={competencyGroup}
          />
        ) : null} */}

          <div className="col-sm-12">
            <FormGroup className="mb-3 text-center">
              <Button
                type="reset"
                text="Reset"
                className="btn btn-secondary me-2"
                onClick={(e) => {
                  setValues(initialValues);
                }}
                disabled={InputFields?.length === 0}
              ></Button>
              {!submitted && InputFields?.length > 0 ? (
                <>
                  <Button
                    type="button"
                    text="Save as Draft"
                    className="btn btn-info me-2"
                    onClick={(e: any) => {
                      e.preventDefault();
                      setSubmitIndex(0);
                      handleSubmit();
                    }}
                  ></Button>
                  <Button
                    type="button"
                    text="Submit"
                    className="btn btn-success"
                    onClick={(e: any) => {
                      e.preventDefault();
                      setSubmitIndex(1);
                      handleSubmit();
                    }}
                  ></Button>
                </>
              ) : (
                <>
                  <Button
                    type="button"
                    text="Save as Draft"
                    className="btn btn-info me-2"
                    onClick={(e: any) => {
                      e.preventDefault();
                      setSubmitIndex(0);
                      handleSubmit();
                    }}
                    disabled
                  ></Button>
                  <Button
                    type="button"
                    text="Submit"
                    className="btn btn-success"
                    onClick={(e: any) => {
                      e.preventDefault();
                      setSubmitIndex(1);
                      handleSubmit();
                    }}
                    disabled
                  ></Button>
                </>
              )}
            </FormGroup>
          </div>
        </div>
      </Form>
    </>
  );
};

export default DynamicForm;
